import { useState } from 'react';

// Format dates to MM/DD/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit', 
    year: 'numeric'
  });
};

/**
 * Converts object to form URL encoded string
 * @param {Object} params - Parameters to encode
 * @returns {string} URL encoded string
 */
const objectToFormUrlEncoded = (params) => {
  const formData = new FormData();
  
  const addToFormData = (key, value, parentKey = '') => {
    const fullKey = parentKey ? `${parentKey}[${key}]` : key;
    
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Handle nested objects
      Object.keys(value).forEach(subKey => {
        addToFormData(subKey, value[subKey], fullKey);
      });
    } else if (value !== null && value !== undefined) {
      formData.append(fullKey, value.toString());
    }
  };

  Object.keys(params).forEach(key => {
    addToFormData(key, params[key]);
  });

  return new URLSearchParams(formData).toString();
};

/**
 * Gets CSRF token from Rails meta tag
 * @returns {string} CSRF token or empty string if not found
 */
const getCSRFToken = () => {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag ? metaTag.getAttribute('content') : '';
};

/**
 * Custom hook for service options API calls with direct payload support
 * @returns {Object} Hook with API functions and loading/error states
 */
export const useServiceOptions = () => {
  const [outboundLoading, setOutboundLoading] = useState(false);
  const [returnLoading, setReturnLoading] = useState(false);
  const [outboundError, setOutboundError] = useState(null);
  const [returnError, setReturnError] = useState(null);
  const [outboundData, setOutboundData] = useState(null);
  const [returnData, setReturnData] = useState(null);

  /**
   * Validates API payload structure
   * @param {Object} payload - API payload to validate
   * @returns {Object} Validation result
   */
  const validatePayload = (payload) => {
    const missing = [];
    
    if (!payload.brand_id) missing.push('brand_id');
    if (!payload.origin) missing.push('origin');
    if (!payload.destination) missing.push('destination');
    
    return {
      isValid: missing.length === 0,
      missing,
      message: missing.length > 0 ? `Missing: ${missing.join(', ')}` : 'Valid'
    };
  };

  /**
   * Fetches service options using direct API payload
   * @param {Object} apiPayload - Direct API payload object
   * @param {boolean} skipValidation - Skip validation checks (default: false)
   * @returns {Promise<Object>} Service options response
   */
  const fetchServiceOptions = async (apiPayload, skipValidation = false) => {
    // Validate payload unless skipped
    if (!skipValidation) {
      const validation = validatePayload(apiPayload);
      if (!validation.isValid) {
        const validationError = `Invalid API payload: ${validation.message}`;
        setOutboundError(validationError);
        throw new Error(validationError);
      }
    }

    setOutboundLoading(true);
    setOutboundError(null);

    try {
      // Build headers
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      // Add auth token if present in payload
      if (apiPayload.authToken) {
        headers['x-user-token'] = apiPayload.authToken;
        // Remove authToken from payload as it's not part of API params
        const { authToken, ...cleanPayload } = apiPayload;
        apiPayload = cleanPayload;
      }

      // Make API call
      const response = await fetch('/api/v5/service_options', {
        method: 'POST',
        headers,
        body: JSON.stringify(apiPayload),
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setOutboundData(result);
      return result;

    } catch (err) {
      console.error('Service options API v5 error:', err);
      const errorMessage = err.message || 'Failed to fetch service options';
      setOutboundError(errorMessage);
      throw err;
    } finally {
      setOutboundLoading(false);
    }
  };

  /**
   * Fetches return service options using direct API payload
   * @param {Object} apiPayload - Direct API payload object
   * @param {boolean} skipValidation - Skip validation checks (default: false)
   * @returns {Promise<Object>} Return service options response
   */
  const fetchReturnServiceOptions = async (apiPayload, skipValidation = false) => {
    // Validate payload unless skipped
    if (!skipValidation) {
      const validation = validatePayload(apiPayload);
      if (!validation.isValid) {
        const validationError = `Invalid API payload: ${validation.message}`;
        setReturnError(validationError);
        throw new Error(validationError);
      }
    }

    setReturnLoading(true);
    setReturnError(null);

    try {

      // Build headers
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      // Add auth token if present in payload
      if (apiPayload.authToken) {
        headers['x-user-token'] = apiPayload.authToken;
        // Remove authToken from payload as it's not part of API params
        const { authToken, ...cleanPayload } = apiPayload;
        apiPayload = cleanPayload;
      }

      // Make API call
      const response = await fetch('/api/v5/service_options', {
        method: 'POST',
        headers,
        body: JSON.stringify(apiPayload),
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setReturnData(result);
      return result;

    } catch (err) {
      console.error('Return service options API v5 error:', err);
      const errorMessage = err.message || 'Failed to fetch return service options';
      setReturnError(errorMessage);
      throw err;
    } finally {
      setReturnLoading(false);
    }
  };

  /**
   * Fetches combined service options using form URL encoded payload for Rails endpoint
   * @param {Object} railsPayload - Rails-formatted payload object
   * @param {boolean} skipValidation - Skip validation checks (default: false)
   * @returns {Promise<Object>} Combined service options response
   */
  const fetchCombinedServiceOptions = async (railsPayload, skipValidation = false) => {
    setOutboundLoading(true);
    setOutboundError(null);

    try {
      // Get CSRF token from meta tag
      const csrfToken = getCSRFToken();
      
      // Add CSRF token to payload
      const payloadWithCSRF = {
        ...railsPayload,
        utf8: '✓'
      };
      
      if (csrfToken) {
        payloadWithCSRF.authenticity_token = csrfToken;
      }
      
      // Convert to form URL encoded format
      const urlEncodedData = objectToFormUrlEncoded(payloadWithCSRF);

      // Make API call
      const response = await fetch('/refactor/v5/ship/combined_services_options', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
          // Include CSRF token in header for Rails
          ...(csrfToken && { 'X-CSRF-Token': csrfToken })
        },
        body: urlEncodedData,
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setOutboundData(result);
      return result;

    } catch (err) {
      console.error('Combined service options API error:', err);
      const errorMessage = err.message || 'Failed to fetch combined service options';
      setOutboundError(errorMessage);
      throw err;
    } finally {
      setOutboundLoading(false);
    }
  };

  return {
    // Main API functions - now accept direct payloads
    fetchServiceOptions,
    fetchReturnServiceOptions, 
    fetchCombinedServiceOptions,
    
    // Loading states
    outboundLoading,
    returnLoading,
    
    // Error states
    outboundError,
    returnError,
    
    // Data states
    outboundData,
    returnData,
    
    // Utilities
    validatePayload,
    getCSRFToken,
    
    // State reset helpers
    resetOutbound: () => {
      setOutboundData(null);
      setOutboundError(null);
      setOutboundLoading(false);
    },
    resetReturn: () => {
      setReturnData(null);
      setReturnError(null);
      setReturnLoading(false);
    },
    resetAll: () => {
      setOutboundData(null);
      setReturnData(null);
      setOutboundError(null);
      setReturnError(null);
      setOutboundLoading(false);
      setReturnLoading(false);
    }
  };
};


const extractCruiseDetails = (address) => {
  if(!address?.cruise || !address?.cruisePort) return address;
  const cruisePort = address.cruisePort;
  return {
      address_1: cruisePort.address_1 || '',
      address_2: cruisePort.address_2 || '',
      city: cruisePort.city || '',
      state: cruisePort.state || '',
      zip5: cruisePort.zip5 || cruisePort.zip || cruisePort.postal_code || '',
      country_code: cruisePort.country_code ||  cruisePort.country ||address.country_code || address.country ||'US',
     // facility_id: address.cruiseLine?.id
  };
}

/**
 * Helper function to create API payload for service options
 * @param {Object} params - Direct API parameters
 * @returns {Object} Formatted API payload
 */
export const createServiceOptionsPayload = ({
  brandId = 'shipplay',
  productIds = [],
  shipDate,
  origin,
  destination,
  authToken = null
}) => {
  const payload = {
    brand_id: brandId,
    product_id: Array.isArray(productIds) ? productIds.join(',') : productIds,
    ship_date: formatDate(shipDate),
    combine: true,
    origin: {
      state: origin.state || '',
      zip: origin.zip || origin.zip5 || '00000',
      country_code: origin.country_code || 'US',
      facility_id: origin.facility_id || '',
      ...(origin.cruise ? extractCruiseDetails(origin) : {}),
    },
    destination: {
      state: destination.state || '',
      zip: destination.zip || destination.zip5 || '00000', 
      country_code: destination.country_code || 'US',
      facility_id: destination.facility_id || '',
      ...(destination.cruise ? extractCruiseDetails(destination) : {}),
    }
  };

  if (authToken) {
    payload.authToken = authToken;
  }

  return payload;
};

/**
 * Helper function to create Rails payload for combined service options
 * @param {Object} params - Rails API parameters  
 * @returns {Object} Formatted Rails payload
 */
export const createCombinedServiceOptionsPayload = ({
  cartId,
  pickupDate,
  arrivalDate,
  hasReturnTrip = false,
  returnPickupDate = null,
  returnArrivalDate = null,
  origin,
  destination,
  travelerData = {},
  source = 'shipplay'
}) => {
  const payload = {
    cart_id: cartId || `cart_${Date.now()}`,
    pickup_date: formatDate(pickupDate),
    arrival_date: formatDate(arrivalDate),
    trip: hasReturnTrip ? 'true' : 'false',
    
    // Origin ship point (o_ prefix)
    o_country: origin.country_code || 'US',
    o_zip: origin.zip5 || origin.zip || '00000',
    o_city: origin.city || '',
    o_state: origin.state || '',
    o_facility_id: origin.facility_id || '',

    // Destination ship point (d_ prefix)
    d_country: destination.country_code || 'US',
    d_zip: destination.zip5 || destination.zip || '00000',
    d_city: destination.city || '',
    d_state: destination.state || '',
    d_facility_id: destination.facility_id || '',
    
    // Traveler data
    traveler: travelerData,
    source
  };

  // Add return trip dates if applicable
  if (hasReturnTrip && returnPickupDate) {
    payload.return_pickup_date = formatDate(returnPickupDate);
    if (returnArrivalDate) {
      payload.return_arrival_date = formatDate(returnArrivalDate);
    }
  }

  return payload;
};